/**
 * Site-wide maintenance warning.
 *
 * An admin broadcasts `system:maintenance` from /api/admin/maintenance-notice
 * and this renders it for everyone connected. The point is the people who
 * would lose work: this box runs one replica with the broadcast pipeline
 * in-process, so a restart drops a live stream AND the recording with it.
 * Telling a host while they can still wrap up is the only useful moment.
 *
 * Two details that are easy to get wrong:
 *
 *  - The server RE-SENDS the notice on every socket connect, because
 *    Socket.IO does not replay events. So this component gets the notice on a
 *    reconnect too, which is why dismissal is keyed on `notice.id` — without
 *    that, a banner the user closed would spring back open on every
 *    connection blip, and a genuinely NEW notice would be swallowed by a
 *    single boolean.
 *  - `null` on the channel means "cleared", not "nothing to do". The admin's
 *    DELETE emits it so the banner disappears without a reload.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { API_URL } from '../utils/serverHealth';
import './MaintenanceBanner.css';

// Signed-out visitors have no socket, so the banner also polls the public
// notice. Plain fetch (not the API client) so a failed poll never toasts.
export const NOTICE_POLL_MS = 60000;
async function fetchPublicNotice() {
  try {
    const res = await fetch(`${API_URL}/maintenance-notice`, { cache: 'no-store' });
    if (!res.ok) return undefined;
    const body = await res.json();
    return body?.notice || null;
  } catch (_) {
    return undefined; // unknown — keep whatever we have
  }
}

/** Human countdown to the restart. Null once it has passed or was never set. */
function formatCountdown(startsAt) {
  if (!startsAt) return null;
  const ms = new Date(startsAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'in under a minute';
  if (mins === 1) return 'in 1 minute';
  if (mins < 60) return `in ${mins} minutes`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `in ${hours}h ${rem}m` : `in ${hours}h`;
}

const MaintenanceBanner = () => {
  const { socket } = useSocket();
  const [notice, setNotice] = useState(null);
  const [dismissedId, setDismissedId] = useState(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (!socket) return undefined;
    const onNotice = (payload) => setNotice(payload || null);
    socket.on('system:maintenance', onNotice);
    return () => socket.off('system:maintenance', onNotice);
  }, [socket]);

  // Poll the public notice on load and every minute (null clears it; a
  // failed poll leaves the current notice alone).
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const next = await fetchPublicNotice();
      if (!cancelled && next !== undefined) setNotice(next);
    };
    poll();
    const id = setInterval(poll, NOTICE_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Re-render once a minute so the countdown stays honest on a tab left open.
  // Only while a countdown is actually showing — no timer otherwise.
  const hasCountdown = !!notice?.startsAt;
  useEffect(() => {
    if (!hasCountdown) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, [hasCountdown]);

  const dismiss = useCallback(() => {
    if (notice?.id) setDismissedId(notice.id);
  }, [notice]);

  if (!notice || notice.id === dismissedId) return null;

  const countdown = formatCountdown(notice.startsAt);
  const severity = ['info', 'warning', 'critical'].includes(notice.severity)
    ? notice.severity
    : 'warning';

  return (
    <div
      className={`maintenance-banner maintenance-banner--${severity}`}
      role="status"
      aria-live="polite"
      data-testid="maintenance-banner"
      data-severity={severity}
    >
      <div className="maintenance-banner-body">
        <span className="maintenance-banner-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </span>
        <span className="maintenance-banner-text">
          {notice.message}
          {countdown && (
            <strong className="maintenance-banner-when" data-testid="maintenance-countdown">
              {' '}— {countdown}
            </strong>
          )}
        </span>
      </div>
      <button
        type="button"
        className="maintenance-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss maintenance notice"
      >
        ×
      </button>
    </div>
  );
};

export default MaintenanceBanner;
